import CustomButton from "@components/common/Button";
import LoadingSpinner from "@components/common/LoadingSpinner";
import NotFound from "@components/common/NotFound";
import { notify } from "@components/common/Notification";
import StateMasterBadge from "@components/StateMasterBadge";
import { checkFormChanges } from "@lib/checkFormChanges";
import { WorkflowStateService } from "@/services/APIs";
import navigationRoutes from "@/utils/navigationRoutes";
import { StateMaster } from "@/utils/types/stateMasters";
import { PageHeader } from "@ant-design/pro-components";
import { useQuery } from "@tanstack/react-query";
import { Card, ColorPicker, Form, Input } from "antd";
import { Save } from "lucide-react";
import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";

const StateMasterFormPage: React.FC = () => {
  const [form] = Form.useForm();
  const navigate = useNavigate();

  const { id } = useParams();
  const isEditMode = !!id;
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [previewStateMaster, setPreviewStateMaster] = useState<
    Partial<StateMaster>
  >({});

  // Use React Query for mutations
  const handleSubmit = async () => {
    const values = await form.validateFields();
    setIsSubmitting(true);

    if (isEditMode) {
      // Check for changes before updating
      if (!checkFormChanges(values, stateMaster!, notify)) {
        setIsSubmitting(false);
        return;
      }

      const response = await WorkflowStateService.updateStateMaster(
        id ?? "",
        values
      );
      if (response) {
        navigate(navigationRoutes.STATE_MASTERS);
      }
    } else {
      const response = await WorkflowStateService.createStateMaster(values);
      if (response) {
        navigate(navigationRoutes.STATE_MASTERS);
      }
    }
    setIsSubmitting(false);
  };

  // Use React Query for data fetching
  const { data: stateMaster, isLoading } = useQuery({
    queryKey: ["state-master", id],
    queryFn: () => WorkflowStateService.getStateMasterById(id ?? ""),
    enabled: !!id,
  });

  useEffect(() => {
    if (isEditMode && stateMaster) {
      form.setFieldsValue(stateMaster);
      setPreviewStateMaster(stateMaster);
    }
  }, [isEditMode, stateMaster, form]);

  if (isEditMode && isLoading) {
    return (
      <div className="flex h-screen items-center justify-center">
        <LoadingSpinner />
      </div>
    );
  }
  if (isEditMode && !stateMaster) {
    return <NotFound />;
  }

  return (
    <Card
      title={
        <PageHeader
          className="custom-page-header"
          title={isEditMode ? "Edit State" : "Add State"}
          subTitle={<StateMasterBadge stateMaster={previewStateMaster ?? {}} />}
          onBack={() => window.history.back()}
          extra={
            <CustomButton
              type="primary"
              icon={<Save size={16} />}
              onClick={handleSubmit}
              loading={isSubmitting}
              disabled={isSubmitting}
              title={isEditMode ? "Save State Changes" : "Save New State"}
            >
              {isSubmitting
                ? "Saving..."
                : isEditMode
                  ? "Save Changes"
                  : "Save"}
            </CustomButton>
          }
        />
      }
    >
      <Form
        form={form}
        layout="vertical"
        onFinish={handleSubmit}
        className="max-w-xl"
        onValuesChange={(_, allValues) => {
          setPreviewStateMaster(allValues);
        }}
      >
        <Form.Item
          name="name"
          label="Name"
          rules={[{ required: true, message: "Please enter state name" }]}
        >
          <Input placeholder="Enter state name" />
        </Form.Item>
        <Form.Item
          name="background_color"
          label="Background Color"
          rules={[{ required: true, message: "Please enter background color" }]}
        >
          <ColorPicker
            value={form.getFieldValue("background_color")}
            onChange={(color: any) => {
              form.setFieldsValue({ background_color: color.toHexString() });
            }}
            showText
            format="hex"
          />
        </Form.Item>
        <Form.Item
          name="text_color"
          label="Text Color"
          rules={[{ required: true, message: "Please enter text color" }]}
        >
          <ColorPicker
            value={form.getFieldValue("text_color")}
            onChange={(color: any) => {
              form.setFieldsValue({ text_color: color.toHexString() });
            }}
            showText
            format="hex"
          />
        </Form.Item>
      </Form>
    </Card>
  );
};

export default StateMasterFormPage;
